import React, { useState, useEffect, useCallback } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { usePathname } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "@/components/auth/useAuth";
import { ThemedText } from "@/components/ThemedText";
import { BrandColors } from "@/constants/ThemeColours";
import { IProductHint, PRODUCT_HINTS } from "@/constants/hints";
import {
  getOrInitFirstSeenTimestamp,
  isWithinFirstTwoDays,
  isHintsDismissedInSession,
  dismissHintsForSession,
  isHintsGloballyEnabled,
  setHintsGloballyEnabled,
  resolveDisplayHints,
} from "@/services/hints-service";

/**
 * Props for the HintBar component.
 */
export interface IHintBarProps {
  /** Optional custom hints catalog override (defaults to PRODUCT_HINTS) */
  hints?: IProductHint[];
  /** Optional callback fired when the user closes or dismisses the bar */
  onDismiss?: () => void;
  /** Optional callback fired when hints are turned off */
  onTurnOff?: () => void;
}


/**
 * Archival Floating Product Hints & Tips Bar.
 * Hovers at the top of the viewport above screen contents.
 *
 * Requirements:
 * - Sourced from docs/user-hints-tips.md
 * - Hovers over contents at the top of the screen
 * - Shows all hints sequentially for the first 2 days from initial launch, then 1 random hint
 * - Clicking outside the hint automatically closes it and only shows again on next startup
 * - Has an explicit close button (✕)
 * - Can be turned off globally in profile
 *
 * @param {IHintBarProps} props - Component props.
 * @returns {React.JSX.Element | null} The rendered floating bar or null.
 */
export function HintBar({
  hints = PRODUCT_HINTS,
  onDismiss,
  onTurnOff,
}: IHintBarProps): React.JSX.Element | null {
  const insets = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();
  const pathname = usePathname();
  const { user, isAuthenticated, isInitialized } = useAuth();

  const isLoginScreen = Boolean(
    pathname && (
      pathname.includes("sign-in") ||
      pathname.includes("login") ||
      pathname.includes("splash")
    )
  );
  const isUnauthenticated = !isInitialized || !isAuthenticated || !user || isLoginScreen;

  const [visible, setVisible] = useState(false);
  const [activeHints, setActiveHints] = useState<IProductHint[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMulti, setIsMulti] = useState(false);

  const checkVisibility = useCallback(async () => {
    if (isUnauthenticated) {
      setVisible(false);
      return;
    }

    try {
      const enabled = await isHintsGloballyEnabled();
      if (!enabled) {
        setVisible(false);
        return;
      }

      if (isHintsDismissedInSession()) {
        setVisible(false);
        return;
      }

      const firstSeen = await getOrInitFirstSeenTimestamp();
      const inFirstTwoDays = isWithinFirstTwoDays(firstSeen);

      const resolved = resolveDisplayHints(hints, inFirstTwoDays);
      setActiveHints(resolved.hints);
      setIsMulti(resolved.isMulti);
      setCurrentIndex(0);
      setVisible(resolved.hints.length > 0);
    } catch (err) {
      console.warn("[HintBar] Error resolving hint visibility:", err);
      setVisible(false);
    }
  }, [hints, isUnauthenticated]);

  useEffect(() => {
    checkVisibility();
  }, [checkVisibility]);

  const handleOutsideClick = () => {
    dismissHintsForSession();
    setVisible(false);
    onDismiss?.();
  };

  const handleClose = () => {
    dismissHintsForSession();
    setVisible(false);
    onDismiss?.();
  };

  const handleTurnOff = async () => {
    await setHintsGloballyEnabled(false);
    setVisible(false);
    onTurnOff?.();
  };

  const handlePrev = () => {
    if (activeHints.length <= 1) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : activeHints.length - 1));
  };

  const handleNext = () => {
    if (activeHints.length <= 1) return;
    setCurrentIndex((prev) => (prev < activeHints.length - 1 ? prev + 1 : 0));
  };

  if (isUnauthenticated || !visible || activeHints.length === 0) {
    return null;
  }

  const currentHint = activeHints[currentIndex] || activeHints[0];
  const topOffset = Math.max(insets.top, Platform.OS === "android" ? 10 : 6);

  return (
    <View style={styles.hoverContainer} pointerEvents="box-none">
      {/* Outside click overlay to dismiss hints until next startup */}
      <TouchableOpacity
        style={styles.outsideDismiss}
        activeOpacity={1}
        onPress={handleOutsideClick}
        accessibilityLabel="Dismiss hint"
      />
      <TouchableOpacity
        activeOpacity={1}
        style={[
          styles.card,
          {
            backgroundColor: BrandColors.blueLinen.hex,
            marginTop: topOffset,
          },
        ]}
        onPress={(e) => {
          if (Platform.OS === "web") {
            e.stopPropagation?.();
          }
        }}
      >
        {/* Top Control Bar: Tip badge & category on left; pagination, turn off, and close on right */}
        <View style={styles.topControlBar}>
          <View style={styles.badgeGroup}>
            <View style={styles.badge}>
              <Ionicons
                name={currentHint.iconName || "bulb-outline"}
                size={13}
                color="#161F36"
                style={{ marginRight: 4 }}
              />
              <ThemedText style={styles.badgeText}>
                {isMulti
                  ? `Tip ${currentIndex + 1}/${activeHints.length}`
                  : "Tip of the Day"}
              </ThemedText>
            </View>

            {screenWidth > 500 ? (
              <ThemedText style={styles.categoryText} numberOfLines={1}>
                • {currentHint.category}
              </ThemedText>
            ) : null}
          </View>

          <View style={styles.actionsGroup}>
            {isMulti && activeHints.length > 1 && (
              <View style={styles.paginationControls}>
                <TouchableOpacity
                  onPress={handlePrev}
                  style={styles.arrowButton}
                  accessibilityLabel="Previous tip"
                  hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
                >
                  <Ionicons name="chevron-back" size={13} color="#161F36" />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleNext}
                  style={styles.arrowButton}
                  accessibilityLabel="Next tip"
                  hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
                >
                  <Ionicons name="chevron-forward" size={13} color="#161F36" />
                </TouchableOpacity>
              </View>
            )}

            <TouchableOpacity
              onPress={handleTurnOff}
              style={styles.turnOffButton}
              accessibilityLabel="Turn off all hints"
              accessibilityHint="Disables product hints until re-enabled in profile"
              hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
            >
              <ThemedText style={styles.turnOffText}>
                Turn Off
              </ThemedText>
            </TouchableOpacity>

            {/* Top-Right Close (X) Button */}
            <TouchableOpacity
              style={styles.closeBtn}
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityLabel="Close hint"
              accessibilityRole="button"
            >
              <ThemedText style={styles.closeBtnText}>✕</ThemedText>
            </TouchableOpacity>
          </View>
        </View>

        {/* Dynamic height body: Title and description flowing naturally */}
        <View style={styles.body}>
          <ThemedText style={styles.bodyText}>
            <ThemedText type="defaultSemiBold" style={styles.titleText}>
              {currentHint.title}:{" "}
            </ThemedText>
            <ThemedText style={styles.descriptionText}>
              {currentHint.description}
            </ThemedText>
          </ThemedText>
        </View>
      </TouchableOpacity>
    </View>
  );
}


const styles = StyleSheet.create({
  hoverContainer: {
    position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 12,
    alignItems: "center",
  },
  outsideDismiss: {
    position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
  },
  card: {
    width: "80%",
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: "rgba(28, 35, 51, 0.25)",
    paddingHorizontal: 14,
    paddingVertical: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 10,
    overflow: "hidden",
    gap: 8,
  },
  topControlBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  badgeGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "rgba(28, 35, 51, 0.12)",
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#161F36",
    letterSpacing: 0.2,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#334155",
  },
  actionsGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  paginationControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  arrowButton: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(28, 35, 51, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    alignItems: "center",
    justifyContent: "center",
  },
  turnOffButton: {
    paddingVertical: 2,
    paddingHorizontal: 4,
  },
  turnOffText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#334155",
    textDecorationLine: "underline",
  },
  closeBtn: {
    width: 22,
    height: 22,
    borderRadius: 4,
    backgroundColor: "rgba(28, 35, 51, 0.12)",
    alignItems: "center",
    justifyContent: "center",
  },
  closeBtnText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#161F36",
    lineHeight: 15,
  },
  body: {
    paddingTop: 2,
  },
  bodyText: {
    fontSize: 12,
    lineHeight: 16,
    color: "#1E293B",
  },
  titleText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#161F36",
  },
  descriptionText: {
    fontSize: 12,
    lineHeight: 16,
    color: "#1E293B",
  },
});
